import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';

import { CwBanner } from '@bbraun/cortex-angular';
import { TranslocoDirective } from '@jsverse/transloco';
import { filter, startWith, Subject } from 'rxjs';

import { Country } from '../../../../definitions/interfaces/country/country';
import { BaseCustomerConfig } from '../../../../definitions/interfaces/customer/customer-config';
import { SwitchViewData } from '../../../../definitions/interfaces/sidebars/switch-view-data';
import { UserConfigsStoreFacade } from '../../../../stores/global-store/user-configs/user-configs-store.facade';
import { SidebarHeartbeatComponent } from '../../../components/sidebar-heartbeat/sidebar-heartbeat.component';
import { SwitchViewSidebarTabStore } from '../../state/switch-view-sidebar-tab-store/switch-view-sidebar-tab-store.service';
import { CountryCustomerSelectionComponent } from '../country-customer-selection/country-customer-selection.component';

@Component({
  selector: 'app-switch-offline-users-tab',
  imports: [
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    CountryCustomerSelectionComponent,
    SidebarHeartbeatComponent,
    CwBanner,
    TranslocoDirective,
  ],
  providers: [
    SwitchViewSidebarTabStore
  ],
  templateUrl: './switch-offline-users-tab.component.html',
  styleUrl: './switch-offline-users-tab.component.sass',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SwitchOfflineUsersTabComponent implements OnInit {
  public isActiveTab = input.required<boolean>();
  public primaryActionClicked = input.required<Subject<void>>();
  public switchView = output<SwitchViewData>();

  public readonly userConfigsFacade = inject(UserConfigsStoreFacade);
  public readonly switchViewSidebarTabStore = inject(SwitchViewSidebarTabStore);
  private readonly destroyRef = inject(DestroyRef);

  public countryControl = new FormControl<Country | undefined>(
    this.userConfigsFacade.currentCountrySignal(),
    { validators: [ Validators.required ]}
  );

  public customerControl = new FormControl<string | BaseCustomerConfig | undefined>(
    undefined,
    { validators: [ Validators.required, this.customerValueValidator() ], nonNullable: true }
  );

  public noCustomersAvailable = computed(() => !this.switchViewSidebarTabStore.allCustomersCount());

  public ngOnInit(): void {
    if (!this.userConfigsFacade.isSuperAdminSignal()) this.countryControl.disable();

    if (this.primaryActionClicked()) {
      this.primaryActionClicked()
        .pipe(
          filter(() => this.isActiveTab()),
          takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.switchToOfflineCustomer());
    }

    this.countryControl.valueChanges.pipe(
      startWith(this.countryControl.value),
      takeUntilDestroyed(this.destroyRef),
      filter((value): value is Country => !!value)
    ).subscribe((country: Country)=> {
      this.switchViewSidebarTabStore.loadOfflineCustomersByCountryCode(country.code);
    });

    this.customerControl.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((value: BaseCustomerConfig | string | undefined ) => {
      if (typeof value === 'string' || value === undefined) {
        this.switchViewSidebarTabStore.setCustomerFilter(value ?? '');
      }
    });
  }

  private customerValueValidator(): ValidatorFn {
    return (control: AbstractControl) : ValidationErrors | null => {
      const value = control.value;

      return (typeof value === 'object') ? null : { invalidCustomerValue: true };
    };
  }

  private switchToOfflineCustomer(): void {
    const country = this.countryControl.value;
    const customer = this.customerControl.value;

    if (!country || !customer || typeof customer === 'string') return;

    this.switchView.emit({
      countryCode: country.code,
      customerErpRef: customer.erpRef,
    });
  }
}
